import { type BuildingStorageQuota } from "../services/buildingStorageQuota";
import { StorageBatchFeedback } from "./storageBatchFeedback";
import { storageState } from "./storagePresentation";
interface Notice {
  kind: "near" | "full";
  remaining: number;
  batchId: string;
}
export interface FeedbackFile {
  batchId: string;
  status: string;
  documentCreated: boolean;
  registrationUncertain: boolean;
}
interface FeedbackState {
  tracker: StorageBatchFeedback;
  notice: Notice | null;
  busy: boolean;
  initialized: boolean;
  unlimitedGeneration: number;
  wasUnlimited: boolean;
}
const sessions = new Map<string, FeedbackState>();
function initialTracker(quota: BuildingStorageQuota | undefined, hasFiles: boolean) {
  return new StorageBatchFeedback(hasFiles ? undefined : quota);
}
export function feedbackSession(
  key: string,
  quota: BuildingStorageQuota | undefined,
  hasFiles: boolean
): FeedbackState {
  let session = sessions.get(key);
  if (!session) {
    session = {
      tracker: initialTracker(quota, hasFiles),
      notice: null,
      busy: false,
      initialized: Boolean(quota || hasFiles),
      unlimitedGeneration: 0,
      wasUnlimited: false,
    };
    sessions.set(key, session);
  }
  if (!session.initialized && (quota || hasFiles)) {
    session.tracker = initialTracker(quota, hasFiles);
    session.initialized = true;
  }
  return session;
}
export function observeFeedback(session: FeedbackState, quota: BuildingStorageQuota) {
  const unlimited = storageState(quota) === "unlimited";
  if (unlimited && !session.wasUnlimited) session.unlimitedGeneration++;
  session.wasUnlimited = unlimited;
  session.tracker.observe(quota);
  if (unlimited) session.notice = null;
}
function batchSettled(files: FeedbackFile[]) {
  return files.some((file) => file.documentCreated) && files.every(fileSettled);
}
function fileSettled(file: FeedbackFile) {
  return !file.registrationUncertain && (file.documentCreated || file.status === "failed");
}
export function completedBatches(session: FeedbackState, files: FeedbackFile[]) {
  return [...new Set(files.map((file) => file.batchId))].filter((batch) => {
    if (session.tracker.handled.has(batch)) return false;
    return batchSettled(files.filter((file) => file.batchId === batch));
  });
}
function applyCompletedBatches(
  session: FeedbackState,
  batches: string[],
  quota: BuildingStorageQuota
) {
  for (const batch of batches) {
    const kind = session.tracker.complete(batch, quota);
    if (kind) session.notice = { kind, remaining: quota.availableBytes ?? 0, batchId: batch };
  }
}
export async function refreshBatches(
  session: FeedbackState,
  batches: string[],
  readQuota: () => Promise<BuildingStorageQuota>
) {
  const generation = session.unlimitedGeneration;
  try {
    const quota = await readQuota();
    if (generation !== session.unlimitedGeneration || session.wasUnlimited) {
      for (const batch of batches) session.tracker.handled.add(batch);
      session.notice = null;
      return;
    }
    applyCompletedBatches(session, batches, quota);
  } finally {
    session.busy = false;
  }
}
